import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Card, CardBody, CardHeader, Input, Button, Badge, Label } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { H3 } from '../../../../AbstractElements';
import axios from 'axios';
import API_BASE_URL from '../../../../Config/api';
import { X } from 'react-feather';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import ExcelJS from 'exceljs';

const OrderTableData = () => {
    const [orderData, setOrderData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [email, setEmail] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedOrderNumbers, setSelectedOrderNumbers] = useState([]);
    const [orderStoreNames, setOrderStoreNames] = useState({});
    const [collections, setCollections] = useState([]);
    const [selectedCollection, setSelectedCollection] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedSeason, setSelectedSeason] = useState('');
    const [orderSeasons, setOrderSeasons] = useState({});
    const [orderStatuses, setOrderStatuses] = useState({});

    // An order matches the season filter when any of its seasons is the selected one
    const orderInSeason = (orderId) =>
        !selectedSeason || (orderSeasons[orderId] || []).includes(selectedSeason);

    // Season options = seasons found on orders + every collection defined on the
    // products (so SS'26, SS'27 ... are always listed, even before the order
    // data loads or before a season has its first order). CORE COLLECTION last.
    // const seasonOptions = [...new Set(Object.values(orderSeasons).flat())].sort((a, b) => { // old: orders only
    const seasonOptions = [...new Set([...Object.values(orderSeasons).flat(), ...collections])].sort((a, b) => {
        if (a === 'CORE COLLECTION') return 1;
        if (b === 'CORE COLLECTION') return -1;
        return a.localeCompare(b, undefined, { numeric: true });
    });
    const sendEmail = async () => {
        if (!email) return alert('Please enter a valid email');

        try {
            const response = await axios.post(`${API_BASE_URL}/api/email-order-summary`, {
                email,
                data: csvData,
            });

            alert(response.data.message || 'Email sent successfully!');
        } catch (err) {
            console.error(err);
            alert('Error sending email');
        }
    };

    // Export collection to Excel
    const handleExportCollection = async () => {
        if (!selectedCollection) {
            alert('Please select a collection first');
            return;
        }

        try {
            // Filter data by selected collection
            const collectionData = filteredData.filter(item => item.collection === selectedCollection);

            if (collectionData.length === 0) {
                alert('No products found for the selected collection');
                return;
            }

            // Get orders for export
            const ordersForExport = selectedOrderNumbers.length > 0
                ? selectedOrderNumbers
                : getAllOrderNumbers();

            // Fetch fresh store names for these orders
            const storeNamesResponse = await axios.post(`${API_BASE_URL}/api/order-store-names`, {
                orderIds: ordersForExport
            });
            const storeNamesMap = storeNamesResponse.data.storeNames || {};

            // Create workbook and worksheet
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet(`${selectedCollection} - Order Summary`);

            // Build headers
            const headers = ['Title', 'Colour', 'Fabric', 'SKU', 'Total Quantity'];
            ordersForExport.forEach(orderId => {
                const storeName = storeNamesMap[orderId] || '';
                headers.push(storeName ? `${orderId} - ${storeName}` : orderId);
            });

            // Add header row
            const headerRow = worksheet.addRow(headers);
            headerRow.font = { bold: true };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFCCCCCC' }
            };

            // Sort filtered data alphabetically by title
            const sortedData = [...collectionData].sort((a, b) => {
                const titleA = (a.title || '').toLowerCase();
                const titleB = (b.title || '').toLowerCase();
                return titleA.localeCompare(titleB);
            });

            // Build data rows
            sortedData.forEach(item => {
                const orderQuantities = {};
                if (item.order_details) {
                    const matches = item.order_details.matchAll(/(\w+)\((\d+)\)/g);
                    for (const match of matches) {
                        const orderId = match[1];
                        const quantity = parseInt(match[2]);
                        orderQuantities[orderId] = (orderQuantities[orderId] || 0) + quantity;
                    }
                }

                // Calculate total quantity from ONLY the orders being exported
                const totalQuantityForExport = ordersForExport.reduce((sum, orderId) => {
                    return sum + (orderQuantities[orderId] || 0);
                }, 0);

                const rowData = [
                    item.title || '',
                    item.colour || '',
                    item.fabric || '',
                    `${item.sku || ''}-${item.size || ''}`,
                    totalQuantityForExport
                ];

                // Add quantities for each order
                ordersForExport.forEach(orderId => {
                    const qty = orderQuantities[orderId] || 0;
                    rowData.push(qty > 0 ? qty : '');
                });

                const excelRow = worksheet.addRow(rowData);

                // Highlight Total Quantity (column E) if less than 5 (including 0)
                if (totalQuantityForExport < 5) {
                    excelRow.getCell(5).fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFFA500' } // Orange
                    };
                }
            });

            // Auto-fit columns
            worksheet.columns.forEach(column => {
                let maxLength = 0;
                column.eachCell({ includeEmpty: true }, cell => {
                    const cellLength = cell.value ? cell.value.toString().length : 10;
                    if (cellLength > maxLength) {
                        maxLength = cellLength;
                    }
                });
                column.width = maxLength < 10 ? 10 : maxLength + 2;
            });

            // Generate Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Download file
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${selectedCollection}-order-summary.xlsx`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error exporting collection:', error);
            alert('Failed to export collection file');
        }
    };

    // Export to Excel with store names and formatting
    const handleExportCSV = async () => {
        try {
            // Get orders for export
            const ordersForExport = selectedOrderNumbers.length > 0
                ? selectedOrderNumbers
                : getAllOrderNumbers();

            // Fetch fresh store names for these orders
            const storeNamesResponse = await axios.post(`${API_BASE_URL}/api/order-store-names`, {
                orderIds: ordersForExport
            });
            const storeNamesMap = storeNamesResponse.data.storeNames || {};
            console.log('Store names for export:', storeNamesMap);

            // Create workbook and worksheet
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Order Summary');

            // Build headers
            const headers = ['Title', 'Colour', 'Fabric', 'SKU', 'Total Quantity'];
            ordersForExport.forEach(orderId => {
                const storeName = storeNamesMap[orderId] || '';
                headers.push(storeName ? `${orderId} - ${storeName}` : orderId);
            });

            // Add header row
            const headerRow = worksheet.addRow(headers);
            headerRow.font = { bold: true };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFCCCCCC' }
            };

            // Sort filtered data alphabetically by title
            const sortedData = [...filteredData].sort((a, b) => {
                const titleA = (a.title || '').toLowerCase();
                const titleB = (b.title || '').toLowerCase();
                return titleA.localeCompare(titleB);
            });

            // Build data rows
            sortedData.forEach(item => {
                const orderQuantities = {};
                if (item.order_details) {
                    const matches = item.order_details.matchAll(/(\w+)\((\d+)\)/g);
                    for (const match of matches) {
                        const orderId = match[1];
                        const quantity = parseInt(match[2]);
                        orderQuantities[orderId] = (orderQuantities[orderId] || 0) + quantity;
                    }
                }

                // Calculate total quantity from ONLY the orders being exported
                const totalQuantityForExport = ordersForExport.reduce((sum, orderId) => {
                    return sum + (orderQuantities[orderId] || 0);
                }, 0);

                // Skip rows with 0 total quantity
                if (totalQuantityForExport === 0) {
                    return;
                }

                const rowData = [
                    item.title || '',
                    item.colour || '',
                    item.fabric || '',
                    `${item.sku || ''}-${item.size || ''}`,
                    totalQuantityForExport
                ];

                // Add quantities for each order
                ordersForExport.forEach(orderId => {
                    const qty = orderQuantities[orderId] || 0;
                    rowData.push(qty > 0 ? qty : '');
                });

                const excelRow = worksheet.addRow(rowData);

                // Highlight Total Quantity (column E) if less than 5 (including 0, but we skip 0 rows anyway)
                if (totalQuantityForExport < 5) {
                    excelRow.getCell(5).fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFFA500' } // Orange
                    };
                }
            });

            // Auto-fit columns
            worksheet.columns.forEach(column => {
                let maxLength = 0;
                column.eachCell({ includeEmpty: true }, cell => {
                    const cellLength = cell.value ? cell.value.toString().length : 10;
                    if (cellLength > maxLength) {
                        maxLength = cellLength;
                    }
                });
                column.width = maxLength < 10 ? 10 : maxLength + 2;
            });

            // Generate Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Download file
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'order-summary.xlsx');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error exporting Excel:', error);
            alert('Failed to export Excel file');
        }
    };

    // Handle individual row selection
    const handleRowSelect = (row) => {
        const rowId = `${row.sku}-${row.size}-${row.title}`;
        const orderIds = row.order_ids ? row.order_ids.split(',').map(id => id.trim()) : [];

        setSelectedRows(prev => {
            const isCurrentlySelected = prev.includes(rowId);

            if (isCurrentlySelected) {
                // Remove this row
                const newSelectedRows = prev.filter(id => id !== rowId);

                // Update selectedOrderNumbers - remove order IDs that no longer have any products selected
                const remainingOrderIds = new Set();
                filteredData.forEach(item => {
                    const itemRowId = `${item.sku}-${item.size}-${item.title}`;
                    if (newSelectedRows.includes(itemRowId)) {
                        const itemOrderIds = item.order_ids ? item.order_ids.split(',').map(id => id.trim()) : [];
                        itemOrderIds.forEach(orderId => remainingOrderIds.add(orderId));
                    }
                });

                setSelectedOrderNumbers(Array.from(remainingOrderIds));
                return newSelectedRows;
            } else {
                // Add this row and its order IDs
                setSelectedOrderNumbers(prev => {
                    const newOrderNumbers = new Set([...prev, ...orderIds]);
                    return Array.from(newOrderNumbers);
                });
                return [...prev, rowId];
            }
        });
    };

    // Handle select all - select all order numbers (filtered by status if selected)
    const handleSelectAll = () => {
        let allOrderNumbers = getAllOrderNumbers();

        // Filter by selected status if one is selected
        if (selectedStatus) {
            allOrderNumbers = allOrderNumbers.filter(orderId =>
                orderStatuses[orderId] === selectedStatus
            );
        }

        // Filter by selected season if one is selected
        allOrderNumbers = allOrderNumbers.filter(orderInSeason);

        setSelectedOrderNumbers(allOrderNumbers);

        // Select all products that belong to the filtered order numbers
        const rowIdsToSelect = [];
        filteredData.forEach(item => {
            const orderIds = item.order_ids ? item.order_ids.split(',').map(id => id.trim()) : [];
            const hasSelectedOrder = orderIds.some(orderId => allOrderNumbers.includes(orderId));

            if (hasSelectedOrder) {
                const rowId = `${item.sku}-${item.size}-${item.title}`;
                rowIdsToSelect.push(rowId);
            }
        });

        setSelectedRows(rowIdsToSelect);
    };

    // Handle deselect all
    const handleDeselectAll = () => {
        setSelectedRows([]);
        setSelectedOrderNumbers([]);
    };

    // Remove individual selection
    const handleRemoveSelection = (rowId) => {
        setSelectedRows(prev => prev.filter(id => id !== rowId));
    };

    // Get selected order objects for display
    const getSelectedOrders = () => {
        return filteredData.filter(item => {
            const rowId = `${item.sku}-${item.size}-${item.title}`;
            return selectedRows.includes(rowId);
        });
    };

    // Get unique order IDs from selected orders
    const getUniqueOrderIds = () => {
        const selectedOrders = getSelectedOrders();
        const allOrderIds = selectedOrders.flatMap(order =>
            order.order_ids ? order.order_ids.split(',').map(id => id.trim()) : []
        );
        return [...new Set(allOrderIds)].sort((a, b) => Number(a) - Number(b));
    };

    // Remove all products that contain a specific order ID
    const handleRemoveOrderId = (orderId) => {
        const rowsToRemove = filteredData
            .filter(item => {
                const orderIds = item.order_ids ? item.order_ids.split(',').map(id => id.trim()) : [];
                return orderIds.includes(orderId);
            })
            .map(item => `${item.sku}-${item.size}-${item.title}`);

        setSelectedRows(prev => prev.filter(id => !rowsToRemove.includes(id)));
        setSelectedOrderNumbers(prev => prev.filter(num => num !== orderId));
    };

    // Get all available order numbers from the data
    const getAllOrderNumbers = () => {
        const allOrderIds = filteredData.flatMap(item =>
            item.order_ids ? item.order_ids.split(',').map(id => id.trim()) : []
        );
        return [...new Set(allOrderIds)].filter(orderInSeason).sort((a, b) => Number(a) - Number(b));
    };

    // Handle order number selection from autocomplete
    const handleOrderNumberSelect = (selected) => {
        setSelectedOrderNumbers(selected);

        // Only select products that belong to the selected order numbers
        const rowIdsToSelect = [];

        filteredData.forEach(item => {
            const orderIds = item.order_ids ? item.order_ids.split(',').map(id => id.trim()) : [];
            // Check if any of the item's order IDs are in the selected list
            const hasSelectedOrder = orderIds.some(orderId => selected.includes(orderId));

            if (hasSelectedOrder) {
                const rowId = `${item.sku}-${item.size}-${item.title}`;
                rowIdsToSelect.push(rowId);
            }
        });

        setSelectedRows(rowIdsToSelect);
    };

    const columns = [
        {
            name: 'Select',
            width: '80px',
            cell: row => {
                const rowId = `${row.sku}-${row.size}-${row.title}`;
                return (
                    <Input
                        type="checkbox"
                        checked={selectedRows.includes(rowId)}
                        onChange={() => handleRowSelect(row)}
                    />
                );
            },
        },
        {
    name: 'Details',
    grow: 2,
    cell: row => (
      <div>
        <h4 className="mb-1">{row.title}</h4>
        <div className="text-muted small mt-1">
          Colour: {row.colour || '—'}, Fabric: {row.fabric || '—'}
        </div>
      </div>
    ),
  },,
        {
            name: 'SKU',
            selector: row => (`${row.sku}-${row.size}`),
            sortable: true,
        },
        {
            name: 'Total Quantity',
            cell: row => {
                // Parse order_details to get quantities per order
                const orderQuantities = {};
                if (row.order_details) {
                    const matches = row.order_details.matchAll(/(\w+)\((\d+)\)/g);
                    for (const match of matches) {
                        const orderId = match[1];
                        const quantity = parseInt(match[2]);
                        orderQuantities[orderId] = (orderQuantities[orderId] || 0) + quantity;
                    }
                }

                // Get order IDs to include based on filters
                const allOrderIds = row.order_ids ? row.order_ids.split(',').map(id => id.trim()) : [];
                let filteredOrderIds = allOrderIds;

                // Filter by status if selected
                if (selectedStatus) {
                    filteredOrderIds = allOrderIds.filter(orderId => orderStatuses[orderId] === selectedStatus);
                }

                // Filter by season if selected
                filteredOrderIds = filteredOrderIds.filter(orderInSeason);

                // Further filter by selected order numbers if any
                if (selectedOrderNumbers.length > 0) {
                    filteredOrderIds = filteredOrderIds.filter(id => selectedOrderNumbers.includes(id));
                }

                // Calculate total quantity for filtered orders only
                const totalQty = filteredOrderIds.reduce((sum, orderId) => {
                    return sum + (orderQuantities[orderId] || 0);
                }, 0);

                return totalQty;
            },
            sortable: true,
        },
        {
            name: 'Order Ids',
            cell: row => {
                const allOrderIds = row.order_ids ? row.order_ids.split(',').map(id => id.trim()) : [];

                // Parse order_details to get quantities per order
                const orderQuantities = {};
                if (row.order_details) {
                    const matches = row.order_details.matchAll(/(\w+)\((\d+)\)/g);
                    for (const match of matches) {
                        const orderId = match[1];
                        const quantity = parseInt(match[2]);
                        orderQuantities[orderId] = (orderQuantities[orderId] || 0) + quantity;
                    }
                }

                // Start with all order IDs
                let displayOrderIds = allOrderIds;

                // Filter by status if selected
                if (selectedStatus) {
                    displayOrderIds = displayOrderIds.filter(id => orderStatuses[id] === selectedStatus);
                }

                // Filter by season if selected
                displayOrderIds = displayOrderIds.filter(orderInSeason);

                // If orders are selected, only show those order IDs
                if (selectedOrderNumbers.length > 0) {
                    displayOrderIds = displayOrderIds.filter(id => selectedOrderNumbers.includes(id));
                }

                return (
                    <div>
                        {displayOrderIds.map(id => (
                            <div key={id}>
                                <a href={`http://localhost:3000/faraadmin/collection-order-form/${id}`} target="_blank" rel="noopener noreferrer">
                                    {id}
                                </a>
                                <span className="text-muted"> - {orderStatuses[id] || 'Unknown'} ({orderQuantities[id] || 0})</span>
                            </div>
                        ))}
                    </div>
                );
            },
            sortable: true,
        }
    ];

    useEffect(() => {
        const fetchOrderSummary = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/order-summary`);
                setOrderData(response.data.data || []);
                setFilteredData(response.data.data || []);

                // Fetch store names for all orders
                const allOrders = response.data.data || [];
                const uniqueOrderIds = [...new Set(
                    allOrders.flatMap(item =>
                        item.order_ids ? item.order_ids.split(',').map(id => id.trim()) : []
                    )
                )];

                // Parse order statuses
                const statusesMap = {};
                allOrders.forEach(item => {
                    if (item.order_statuses) {
                        const statusPairs = item.order_statuses.split(',');
                        statusPairs.forEach(pair => {
                            const [orderId, status] = pair.split(':');
                            if (orderId && status) {
                                statusesMap[orderId.trim()] = status.trim();
                            }
                        });
                    }
                });
                setOrderStatuses(statusesMap);

                // Fetch store names for these orders
                const storeNamesResponse = await axios.post(`${API_BASE_URL}/api/order-store-names`, {
                    orderIds: uniqueOrderIds
                });
                console.log('Store names received:', storeNamesResponse.data.storeNames);
                setOrderStoreNames(storeNamesResponse.data.storeNames || {});
            } catch (error) {
                console.error('Error fetching order summary:', error);
            } finally {
                setLoading(false);
            }
        };

        const fetchOrderSeasons = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/order-seasons`);
                setOrderSeasons(response.data.orderSeasons || {});
            } catch (error) {
                console.error('Error fetching order seasons:', error);
            }
        };

        const fetchCollections = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/collections`);
                setCollections(response.data.collections || []);
            } catch (error) {
                console.error('Error fetching collections:', error);
            }
        };

        fetchOrderSummary();
        fetchOrderSeasons();
        fetchCollections();
    }, []);
    useEffect(() => {
        let filtered = orderData.filter(item =>
            (item.title || '').toLowerCase().includes(searchText.toLowerCase()) ||
            (item.sku || '').toLowerCase().includes(searchText.toLowerCase()) ||
            (item.size || '').toLowerCase().includes(searchText.toLowerCase())
        );

        // Further filter by selected order numbers if any are selected
        if (selectedOrderNumbers.length > 0) {
            filtered = filtered.filter(item => {
                const orderIds = item.order_ids ? item.order_ids.split(',').map(id => id.trim()) : [];
                return orderIds.some(orderId => selectedOrderNumbers.includes(orderId));
            });
        }

        // Filter by status - only show products that have orders with the selected status
        if (selectedStatus) {
            filtered = filtered.filter(item => {
                const orderIds = item.order_ids ? item.order_ids.split(',').map(id => id.trim()) : [];
                return orderIds.some(orderId => orderStatuses[orderId] === selectedStatus);
            });
        }

        // Filter by season - only show products that have orders in the selected season
        if (selectedSeason) {
            filtered = filtered.filter(item => {
                const orderIds = item.order_ids ? item.order_ids.split(',').map(id => id.trim()) : [];
                return orderIds.some(orderId => (orderSeasons[orderId] || []).includes(selectedSeason));
            });
        }

        setFilteredData(filtered);
    }, [searchText, orderData, selectedOrderNumbers, selectedStatus, orderStatuses, selectedSeason, orderSeasons]);
    return (
        <Fragment>
            <Card>
                <CardHeader className='pb-0 d-flex justify-content-between align-items-center'>
                    <H3>{'Order history'}</H3>
                    <div className="d-flex gap-2 align-items-center">
                        <span className="text-muted">
                            {selectedOrderNumbers.length > 0 ? `${selectedOrderNumbers.length} order(s) selected` : 'No orders selected'}
                        </span>
                        <Button color="secondary" size="sm" onClick={handleSelectAll}>
                            Select All
                        </Button>
                        <Button color="secondary" size="sm" onClick={handleDeselectAll}>
                            Deselect All
                        </Button>
                        <Button color="primary" onClick={handleExportCSV}>
                            Export to Excel
                        </Button>
                    </div>
                </CardHeader>
                <CardBody>
                    <div className="mb-3">
                        <Label className="fw-bold mb-2">Filter by Status</Label>
                        <Input
                            type="select"
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            style={{ maxWidth: 300 }}
                        >
                            <option value="">All Statuses</option>
                            <option value="Production">Production</option>
                            <option value="Immediate">Immediate</option>
                            <option value="Shipment">Shipment</option>
                            <option value="Cancelled">Cancelled</option>
                        </Input>
                        {selectedStatus && (
                            <small className="text-muted d-block mt-1">
                                Click "Select All" to select all orders with "{selectedStatus}" status
                            </small>
                        )}
                    </div>

                    <div className="mb-3">
                        <Label className="fw-bold mb-2">Filter by Season</Label>
                        <Input
                            type="select"
                            value={selectedSeason}
                            onChange={(e) => setSelectedSeason(e.target.value)}
                            style={{ maxWidth: 300 }}
                        >
                            <option value="">All Seasons</option>
                            {seasonOptions.map((season) => (
                                <option key={season} value={season}>
                                    {season}
                                </option>
                            ))}
                        </Input>
                        {selectedSeason && (
                            <small className="text-muted d-block mt-1">
                                Showing only orders placed in {selectedSeason}
                            </small>
                        )}
                    </div>

                    <div className="mb-3">
                        <Label className="fw-bold mb-2">Select Orders by Order Number</Label>
                        <Typeahead
                            id="order-number-autocomplete"
                            multiple
                            options={getAllOrderNumbers()}
                            placeholder="Type to search and select order numbers..."
                            selected={selectedOrderNumbers}
                            onChange={handleOrderNumberSelect}
                            clearButton
                        />
                    </div>

                    <div className="mb-3">
                        <Label className="fw-bold mb-2">Export by Collection</Label>
                        <div className="d-flex gap-2 align-items-center">
                            <Input
                                type="select"
                                value={selectedCollection}
                                onChange={(e) => setSelectedCollection(e.target.value)}
                                style={{ maxWidth: 300 }}
                            >
                                <option value="">Select a collection...</option>
                                {collections.map((collection) => (
                                    <option key={collection} value={collection}>
                                        {collection}
                                    </option>
                                ))}
                            </Input>
                            <Button
                                color="success"
                                onClick={handleExportCollection}
                                disabled={!selectedCollection}
                            >
                                Export Collection
                            </Button>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                        <Input
                        type="text"
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ maxWidth: 300 }}
                        />

                        <div className="d-flex align-items-center gap-2">
                        <Input
                            type="email"
                            placeholder="Enter email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={{ maxWidth: 250 }}
                        />
                        <Button color="primary" onClick={sendEmail} style={{ whiteSpace: 'nowrap' }}>
                            Email List
                        </Button>
                        </div>
                    </div>

                    <div className="order-history table-responsive">
                        <DataTable
                        data={filteredData}
                        columns={columns}
                        pagination
                        progressPending={loading}
                        highlightOnHover
                        striped
                        responsive
                        />
                    </div>
                    </CardBody>
            </Card>
        </Fragment>
    );
};

export default OrderTableData;
